/*
 * /vat-calculator — страница. Арифметика — core.js (копейки BigInt, ставка дробью), сумма
 * прописью — money-words.js, ставки и фразы стран — resources/data/vat/countries.json,
 * которые сервер кладёт в window.__VAT вместе со строками локали.
 *
 * Первый экран рисует сервер (пример 100 000 ₽ уже посчитан в разметке); модуль только
 * оживляет его: живой пересчёт на каждый ввод, ссылка-состояние в hash, копирование
 * готовых строк. Остальное — в ящиках под ответом: фразы для документов, таблица позиций,
 * пересчёт прайса, для России — монитор порогов УСН, сравнение 5/7 % с 22 % и переход
 * 2025 → 2026. Разбор — docs/vat-calculator.md.
 */
import * as V from './core.js';
import '../money-words.js';

const MW = globalThis.MoneyWords;
const SLUG = 'vat-calculator';
const W = window.__VAT || {};
const CT = W.countries || {};
const I = W.i18n || {};
const PAGE = W.locale || 'en';
const LOC = (window.__intlLocale && window.__intlLocale()) || PAGE;
const RTL = document.documentElement.getAttribute('dir') === 'rtl';
const $ = (s, r = document) => r.querySelector(s);
const $$ = (s, r = document) => Array.from(r.querySelectorAll(s));
const tr = (k, rep = {}) => String(I[k] != null ? I[k] : k).replace(/:([a-z_][a-z_0-9]*)/g, (m, n) => (rep[n] != null ? rep[n] : m));
const esc = (s) => String(s == null ? '' : s).replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]));
// Число в арабском тексте: «−18 032,79» не должно развернуться справа налево.
const iso = (s) => (RTL ? '\u2066' + s + '\u2069' : s);

function ae(action, extra) { try { window.__ae && window.__ae('feature_use', Object.assign({ slug: SLUG, action }, extra || {})); } catch (e) { /* optional */ } }
function stage(name, meta) { try { window.__stage && window.__stage(name, meta || {}); } catch (e) { /* optional */ } }
function toast(msg, type) { try { window.__toast && window.__toast(msg, type || 'info'); } catch (e) { /* optional */ } }
function copy(text, what) {
    text = String(text == null ? '' : text).replace(/[\u2066-\u2069]/g, '');
    const done = (ok) => { toast(ok ? tr('copied') : tr('copy_fail'), ok ? 'success' : 'error'); if (ok) ae('copy', { what }); };
    if (window.__copyText) window.__copyText(text).then(done);
    else if (navigator.clipboard) navigator.clipboard.writeText(text).then(() => done(true), () => done(false));
    else done(false);
}

/* ───────────────────────────── формат ───────────────────────────── */

const nfc = new Map();
function nf(loc, o) {
    const key = loc + JSON.stringify(o);
    if (!nfc.has(key)) {
        try { nfc.set(key, new Intl.NumberFormat(loc, o)); }
        catch (e) { nfc.set(key, new Intl.NumberFormat('en', o)); }
    }
    return nfc.get(key);
}
/* Деньги страны показываются её числовой записью, если страна говорит на языке страницы
   (es-MX на испанской странице — «1,234.56», es-AR — «1.234,56»), иначе — записью страницы. */
function numLoc(c) { const l = String(c.nl || '').split('-')[0]; return l === PAGE ? c.nl : LOC; }
function money(minor, c, loc) {
    const s = V.minorToDecimal(minor, c.dec);
    try { return iso(nf(loc || numLoc(c), { style: 'currency', currency: c.cur, minimumFractionDigits: c.dec, maximumFractionDigits: c.dec }).format(s)); }
    catch (e) { return iso(s + ' ' + c.cur); }
}
function plain(minor, c, loc) {
    return iso(nf(loc || numLoc(c), { minimumFractionDigits: c.dec, maximumFractionDigits: c.dec }).format(V.minorToDecimal(minor, c.dec)));
}
function pct(ppm, loc) {
    return iso(nf(loc || LOC, { style: 'percent', maximumFractionDigits: 4 }).format(Number(ppm) / 1e6));
}
function pctNum(ppm, loc) { return nf(loc || LOC, { maximumFractionDigits: 4 }).format(Number(ppm) / 1e4); }
function rub(n, c) { return iso(nf(numLoc(c), { style: 'currency', currency: c.cur, maximumFractionDigits: 0 }).format(Math.round(n))); }
/* Как в этой записи пишут числа: для разбора ввода (core.parseAmount). */
function sepOf(loc) {
    try {
        const parts = new Intl.NumberFormat(loc).formatToParts(1234567.5);
        const dec = (parts.find((p) => p.type === 'decimal') || {}).value;
        const grp = (parts.find((p) => p.type === 'group') || {}).value;
        if (dec === '.') return 'en';
        if (grp === '.') return 'de';
        return 'ru';
    } catch (e) { return 'ru'; }
}

/* Разделитель столбцов CSV — тот, которого ждёт Excel человека (разделитель списка Windows):
   у записи с десятичной запятой — «;», с точкой — «,». Без этого CSV, сохранённый для Великобритании
   или США с «;», открывался в Excel одним столбцом (QA 23.10.2026). Исключения сняты с CultureInfo
   Windows: es-PE, de-CH и арабские записи — точка и «;», en-ZA — запятая и «,» (ячейка с запятой
   уходит в кавычках). */
function csvDelim(c) {
    const s = sepOf(numLoc(c));
    if (s !== 'en') return S.c === 'ZA' ? ',' : ';';
    return (S.c === 'PE' || S.c === 'CH' || PAGE === 'ar') ? ';' : ',';
}

/* ───────────────────────────── состояние ───────────────────────────── */

const S = {
    c: W.country || 'RU',
    mode: 'extract',
    amt: W.example || '100000',
    rate: null,           // индекс ставки страны или 'custom'
    custom: '',
    split: 'intra',       // Индия: CGST+SGST внутри штата / IGST между штатами
    inside: null,         // своя ставка «по дентро» (Бразилия: ICMS, ISS) — null = как у страны
    words: { fig: 'gross', style: 'digits_words', minor: 'digits', cap: true },
};
let R = null;             // последний расчёт
let touched = false;      // tool_complete — только за свой расчёт, не за пример
const country = () => CT[S.c] || CT[W.country] || CT.RU;
/* Своя ставка «внутри цены»: по умолчанию — как принято в стране (у Бразилии ICMS и ISS), выбор человека важнее. */
const customInside = (c) => (S.inside == null ? !!c.inside : S.inside);
/* Имя налога у ставки: у Бразилии ставки разных налогов (ICMS, CBS/IBS). */
const taxOf = (c, cr) => (cr && cr.src && cr.src.tax) || c.tax;
/* Фразы для документа: у ставки могут быть свои (база ICMS — сумма С налогом, у CBS/IBS — без). */
const phOf = (c, cr) => Object.assign({}, c.ph || {}, (cr && cr.src && cr.src.ph) || {});

function rateList(c) {
    return (c.rates || []).map((r, i) => {
        let spec;
        if (r.in) spec = { r: r.r, inside: true };
        else if (c.split === 'in' && Number(r.r) > 0) {
            const half = String(Number(r.r) / 2);
            spec = S.split === 'intra' ? { parts: [{ id: 'CGST', r: half }, { id: 'SGST', r: half }] } : { parts: [{ id: 'IGST', r: r.r }] };
        } else if (r.parts) spec = { parts: r.parts.map(([id, x]) => ({ id, r: x })) };
        else if (r.dpp) spec = { r: r.r, dpp: { num: r.dpp[0], den: r.dpp[1] } };
        else spec = { r: r.r };
        return { i, src: r, rate: V.makeRate(spec) };
    });
}
function currentRate(c) {
    if (S.rate === 'custom') {
        const v = String(S.custom).replace(',', '.').replace(/[^\d.]/g, '');
        if (!v || !isFinite(+v) || +v < 0 || +v > 1000) return null;
        const inside = customInside(c);
        try { return { i: 'custom', src: { r: v, in: inside ? 1 : 0 }, rate: V.makeRate(inside ? { r: v, inside: true } : v) }; } catch (e) { return null; }
    }
    const list = rateList(c);
    if (!list.length) return null;
    const k = S.rate == null ? Math.max(0, list.findIndex((x) => x.src.d)) : Number(S.rate);
    return list[k] || list[0];
}
/** Эффективный процент (у DPP 11/12 это 11 %, а не 12 %). */
function effPpm(rate) {
    const e = V.effective(rate);
    return e.n * V.FULL / e.d;
}

/* ───────────────────────────── ввод ───────────────────────────── */

function readAmount(c) {
    const r = V.parseAmount(S.amt, { sep: sepOf(numLoc(c)), decimals: c.dec });
    return r;
}

function renderRates() {
    const c = country();
    const box = $('#vc-rates');
    if (!box) return;
    const list = rateList(c);
    const cur = currentRate(c);
    const chips = list.map((x) => {
        const on = cur && cur.i === x.i;
        const sub = x.src.lbl ? x.src.lbl : x.src.t ? tr('chip_' + x.src.t[0]) : '';
        const shown = x.src.dpp ? pct(effPpm(x.rate)) : pct(x.rate.ppm);
        return `<button type="button" class="tc-chip vc-chip${on ? ' on' : ''}" data-rate="${x.i}" aria-pressed="${on}"><b>${shown}</b>${sub && !sub.startsWith('chip_') ? `<span>${esc(sub)}</span>` : ''}</button>`;
    });
    chips.push(`<button type="button" class="tc-chip vc-chip${S.rate === 'custom' ? ' on' : ''}" data-rate="custom" aria-pressed="${S.rate === 'custom'}"><b>${esc(tr('rate_custom'))}</b></button>`);
    box.innerHTML = chips.join('');
    $('#vc-custom-wrap').hidden = S.rate !== 'custom';
    const ins = $('#vc-inside');
    if (ins) {
        ins.hidden = S.rate !== 'custom' || c.inside == null;
        const on = customInside(c) ? 'in' : 'out';
        $$('#vc-inside .tc-seg-b').forEach((b) => { const x = b.dataset.inside === on; b.classList.toggle('on', x); b.setAttribute('aria-pressed', x); });
    }
    $('#vc-split').hidden = c.split !== 'in';
    $$('#vc-split .tc-seg-b').forEach((b) => { const on = b.dataset.split === S.split; b.classList.toggle('on', on); b.setAttribute('aria-pressed', on); });
    // Что облагается по выбранной ставке — словами, одной строкой под фишками.
    const hint = $('#vc-rate-hint');
    if (hint) {
        const t = cur && cur.src.t ? cur.src.t.map((k) => tr('cat_' + k)).filter((s) => !s.startsWith('cat_')) : [];
        hint.textContent = t.join(', ');
        hint.hidden = !t.length;
    }
    $('#vc-cur').textContent = curSymbol(c);
}
function curSymbol(c) {
    try {
        const p = new Intl.NumberFormat(numLoc(c), { style: 'currency', currency: c.cur }).formatToParts(1);
        return (p.find((x) => x.type === 'currency') || {}).value || c.cur;
    } catch (e) { return c.cur; }
}

/* ───────────────────────────── расчёт и ответ ───────────────────────────── */

function compute() {
    const c = country();
    const cr = currentRate(c);
    const p = readAmount(c);
    const msg = $('#vc-msg');
    msg.hidden = true; msg.textContent = '';
    if (c.kind === 'not_vat' && S.rate !== 'custom') { R = null; paintNone('not_vat'); return; }
    if (!cr) { R = null; paintNone(S.rate === 'custom' ? 'rate_bad' : 'no_rates'); return; }
    if (!p.ok) { R = null; paintNone(p.empty ? 'amount_empty' : 'amount_bad'); return; }
    if (S.mode === 'tax' && cr.rate.ppm === 0n) { R = null; paintNone('rate_zero_tax'); return; }
    const res = V.calc(S.mode, p.minor, cr.rate);
    R = { c, cr, res, input: p };
    if (p.expr) { msg.hidden = false; msg.className = 'tc-msg'; msg.textContent = tr('expr_result', { v: money(p.minor, c) }); }
    paint();
    if (touched) scheduleComplete();
}

let noneTimer = 0, lastNone = '';
function paintNone(why) {
    // Отказ — событием (без самой суммы): что именно не получилось у людей. Ввод по буквам
    // даёт «amount_bad» на каждом нажатии — шлём только то, на чём человек остановился.
    clearTimeout(noneTimer);
    noneTimer = setTimeout(() => {
        if (R || lastNone === why + S.c) return;
        lastNone = why + S.c;
        try { window.__aeDiagnostic && window.__aeDiagnostic({ issue: 'vat_' + why, country: S.c, mode: S.mode }); } catch (e) { /* optional */ }
    }, 2500);
    $('#vc-ans-l').textContent = tr('ans_label', { tax: country().tax || '', rate: '' }).trim();
    $('#vc-ans-v').textContent = '—';
    $('#vc-ans-sub').textContent = tr('why_' + why);
    for (const id of ['#vc-net', '#vc-tax', '#vc-gross']) $(id).textContent = '—';
    $('#vc-line').textContent = '';
    $('#vc-parts').innerHTML = '';
    paintLive();
    paintWords();
    paintPhrases();
}

function paint() {
    const { c, cr, res } = R;
    const rp = cr.src.dpp ? effPpm(cr.rate) : cr.rate.ppm;
    const rt = pct(cr.rate.ppm);
    const tax = taxOf(c, cr);
    $('#vc-ans-l').textContent = tr('ans_label', { tax, rate: cr.src.dpp ? pct(rp) : rt });
    $('#vc-ans-v').textContent = money(res.tax, c);
    $('#vc-ans-sub').textContent = formula();
    $('#vc-net').textContent = money(res.net, c);
    $('#vc-tax').textContent = money(res.tax, c);
    $('#vc-gross').textContent = money(res.gross, c);
    $('#vc-net-l').textContent = tr('st_net', { tax });
    $('#vc-tax-l').textContent = tr('st_tax', { tax, rate: cr.src.dpp ? pct(rp) : rt });
    $('#vc-gross-l').textContent = tr('st_gross', { tax });
    $('#vc-amt-l').textContent = tr('amt_' + S.mode, { tax });
    // Части ставки и «иная база».
    const parts = [];
    if (res.dppBase != null) parts.push(`<li>${esc(tr('dpp_line', { base: money(res.dppBase, c), num: cr.src.dpp[0], den: cr.src.dpp[1] }))}</li>`);
    if (res.parts) for (const pt of res.parts) parts.push(`<li><span>${esc(pt.id)} ${pct(pt.ppm)}</span><b>${money(pt.tax, c)}</b></li>`);
    $('#vc-parts').innerHTML = parts.length ? `<ul class="vc-parts">${parts.join('')}</ul>` : '';
    $('#vc-line').textContent = readyLine();
    paintLive();
    paintWords();
    paintPhrases();
    writeHash();
}

/* Одна колонка (телефон): ответ — под полем суммы, а не под всей рейкой. С открытой
   клавиатурой видно только поле и то, что сразу под ним: человек набирает сумму и
   видит налог, не листая (QA 23.10.2026 — на 390×844 ответ стоял на 1350 px). */
function paintLive() {
    const el = $('#vc-live');
    if (!el) return;
    if (!R) { el.textContent = ''; el.hidden = true; return; }
    const { c, cr, res } = R;
    const tax = taxOf(c, cr);
    const rate = cr.src.dpp ? pct(effPpm(cr.rate)) : pct(cr.rate.ppm);
    const head = S.mode === 'tax'
        ? [tr('st_net', { tax }), money(res.net, c)]
        : [tr('ans_label', { tax, rate }), money(res.tax, c)];
    const tail = S.mode === 'extract' ? [tr('st_net', { tax }), money(res.net, c)] : [tr('st_gross', { tax }), money(res.gross, c)];
    el.innerHTML = `<span class="vc-live-l">${esc(head[0])}</span> <b>${head[1]}</b><span class="vc-live-s"> · ${esc(tail[0])}: ${tail[1]}</span>`;
    el.hidden = false;
}

/* В арабском абзаце выражение «1,000.00 − 130.43 = 869.57» без изоляции встаёт задом наперёд:
   каждое выражение целиком — в LTR-изоляцию, слова вокруг остаются справа налево. */
const MATH_RUN = /(?:\u2066[^\u2069]*\u2069|\d[\d.,\u00a0\u202f]*)(?:\s*[×÷/=+−-]\s*(?:\u2066[^\u2069]*\u2069|\d[\d.,\u00a0\u202f]*))+/g;
const isoMath = (t) => (RTL ? t.replace(MATH_RUN, (m) => '\u2066' + m + '\u2069') : t);
/** «100 000,00 × 22 / 122 = 18 032,79» — и словами, чем это считается. */
function formula() { return isoMath(formulaText()); }
function formulaText() {
    const { c, cr, res } = R;
    const loc = numLoc(c);
    const p = pctNum(cr.src.dpp ? effPpm(cr.rate) : cr.rate.ppm, loc);
    const hundred = nf(loc, { maximumFractionDigits: 4 }).format(100 + Number(cr.src.dpp ? effPpm(cr.rate) : cr.rate.ppm) / 1e4);
    if (cr.rate.inside) {
        // «по дентро»: ставка от суммы С налогом — своя запись формулы (ICMS 18 % из R$ 1.000 = R$ 180)
        if (S.mode === 'extract') return tr('f_extract_in', { a: plain(res.gross, c), r: p, tax: plain(res.tax, c), net: plain(res.net, c) });
        if (S.mode === 'add') return tr('f_add_in', { a: plain(res.net, c), r: p, tax: plain(res.tax, c), gross: plain(res.gross, c) });
        return tr('f_tax_in', { a: plain(res.tax, c), r: p, net: plain(res.net, c), gross: plain(res.gross, c) });
    }
    if (S.mode === 'extract') return tr('f_extract', { a: plain(res.gross, c), r: p, d: hundred, tax: plain(res.tax, c), net: plain(res.net, c) });
    if (S.mode === 'add') return tr('f_add', { a: plain(res.net, c), r: p, tax: plain(res.tax, c), gross: plain(res.gross, c) });
    return tr('f_tax', { a: plain(res.tax, c), r: p, net: plain(res.net, c), gross: plain(res.gross, c) });
}

/* ───────────────────────────── фразы ───────────────────────────── */

/* Фраза — на языке страны, числа — её записью: это текст для документа, а не для страницы. */
function fill(tpl, c, extra = {}) {
    const { cr, res } = R;
    const loc = c.nl;
    const rp = cr.src.dpp ? effPpm(cr.rate) : cr.rate.ppm;
    const rep = {
        rate: pct(cr.src.dpp ? rp : cr.rate.ppm, loc),
        tax: money(res.tax, c, loc), net: money(res.net, c, loc), gross: money(res.gross, c, loc),
        base: res.dppBase != null ? money(res.dppBase, c, loc) : '',
        ...extra,
    };
    return String(tpl).replace(/:([a-z_][a-z_0-9]*)/g, (m, n) => (rep[n] != null ? rep[n] : m));
}
function readyLine() {
    const c = R.c, ph = phOf(c, R.cr);
    if (S.mode === 'add' && ph.excl) return fill(ph.excl, c);
    if (ph.incl) return fill(ph.incl, c);
    return tr('ans_label', { tax: taxOf(c, R.cr), rate: pct(R.cr.rate.ppm) }) + ': ' + money(R.res.tax, c);
}
function wordsOf(minor, c, o = {}) {
    if (!c.words || !MW || !MW.supports(c.words, c.cur)) return null;
    try {
        return MW.amount(V.minorToDecimal(minor, c.dec), {
            lang: c.words, currency: c.cur, decimals: c.dec,
            style: o.style || 'words', minor: o.minor, capital: o.cap !== false, numLocale: c.nl,
        });
    } catch (e) { return null; }
}

function phraseItems() {
    if (!R) return [];
    const c = R.c, ph = phOf(c, R.cr);
    const out = [];
    const add = (text, note, key) => { if (text) out.push({ text, note, key }); };
    const W1 = (m) => wordsOf(m, c, { style: 'digits_words', minor: 'digits', cap: true });
    if (ph.incl) add(fill(ph.incl, c), tr('ph_incl_note'), 'incl');
    if (ph.incl_full) add(fill(ph.incl_full, c), tr('ph_incl_full_note'), 'incl_full');
    if (ph.contract && W1(R.res.gross)) add(fill(ph.contract, c, { gross_w: W1(R.res.gross), tax_w: W1(R.res.tax), net_w: W1(R.res.net) }), tr('ph_contract_note'), 'contract');
    if (ph.excl_full) add(fill(ph.excl_full, c), tr('ph_excl_full_note'), 'excl_full');
    // «zzgl. 19 % MwSt.: 159,66 €» без базы читается как «к этой сумме ещё налог» — база впереди.
    else if (ph.excl) add(money(R.res.net, c, c.nl) + ' ' + fill(ph.excl, c), tr('ph_excl_note'), 'excl');
    if (ph.contract_net && W1(R.res.net)) add(fill(ph.contract_net, c, { gross_w: W1(R.res.gross), tax_w: W1(R.res.tax), net_w: W1(R.res.net) }), tr('ph_contract_net_note'), 'contract_net');
    if (ph.lbl) add(ph.lbl.map((l, i) => fill(l, c) + ': ' + money([R.res.net, R.res.tax, R.res.gross][i], c, c.nl)).join('\n'), tr('ph_lbl_note'), 'lbl');
    if (ph.dpp && R.res.dppBase != null) add(fill(ph.dpp, c), tr('ph_dpp_note'), 'dpp');
    if (ph.title) add(ph.title, tr('ph_title_note'), 'title');
    for (const [i, e] of (ph.exempt || []).entries()) add(e, tr((ph.exempt_notes && ph.exempt_notes[i]) || 'ph_exempt_note'), 'exempt' + i);
    return out;
}
function paintPhrases() {
    const box = $('#vc-phr-list');
    if (!box) return;
    const items = phraseItems();
    box.innerHTML = items.length ? items.map((p, i) => `<div class="vc-phr"><p class="vc-phr-t" lang="${esc(String(R.c.nl || '').split('-')[0])}">${esc(p.text).replace(/\n/g, '<br>')}</p>${p.note && !String(p.note).startsWith('ph_') ? `<p class="tc-hint">${esc(p.note)}</p>` : ''}<button type="button" class="tc-btn sm ghost" data-copy-phr="${i}">${esc(tr('btn_copy'))}</button></div>`).join('')
        : `<p class="tc-hint">${esc(tr('ph_none'))}</p>`;
    box._items = items;
}

/* ───────────────────────────── прописью ───────────────────────────── */

function paintWords() {
    const box = $('#vc-words');
    if (!box) return;
    const c = country();
    const ok = !!(R && c.words && MW && MW.supports(c.words, c.cur));
    box.hidden = !ok;
    if (!ok) return;
    const m = { gross: R.res.gross, tax: R.res.tax, net: R.res.net }[S.words.fig];
    const w = wordsOf(m, c, S.words);
    $('#vc-words-out').textContent = w || '';
    $('#vc-words-out').setAttribute('lang', c.words);
    $$('#vc-words-fig .tc-seg-b').forEach((b) => { const on = b.dataset.fig === S.words.fig; b.classList.toggle('on', on); b.setAttribute('aria-pressed', on); });
    $('#vc-words-minor').closest('.tc-f').hidden = c.dec === 0;
}

/* ───────────────────────────── ссылка-состояние ───────────────────────────── */

function writeHash() {
    if (!touched) return;
    const q = new URLSearchParams();
    q.set('c', S.c); q.set('m', S.mode); q.set('a', S.amt);
    const cr = currentRate(country());
    if (S.rate === 'custom') { q.set('r', 'x' + S.custom); if (S.inside != null) q.set('i', S.inside ? '1' : '0'); }
    else if (cr) q.set('r', String(cr.i));
    if (country().split === 'in') q.set('s', S.split);
    try { history.replaceState(null, '', location.pathname + location.search + '#' + q.toString()); } catch (e) { /* sandboxed */ }
}
function readHash() {
    if (!location.hash || location.hash.length < 3) return false;
    try {
        const q = new URLSearchParams(location.hash.slice(1));
        const c = q.get('c');
        if (c && CT[c]) S.c = c;
        const m = q.get('m');
        if (['extract', 'add', 'tax'].includes(m)) S.mode = m;
        const a = q.get('a');
        if (a != null) S.amt = a.slice(0, 120);
        const r = q.get('r');
        if (r && r[0] === 'x') { S.rate = 'custom'; S.custom = r.slice(1, 12); }
        else if (r != null && /^\d+$/.test(r) && (CT[S.c].rates || [])[+r]) S.rate = +r;
        const s = q.get('s');
        if (s === 'inter' || s === 'intra') S.split = s;
        const ii = q.get('i');
        if (ii === '1' || ii === '0') S.inside = ii === '1';
        return true;
    } catch (e) { return false; }
}
function shareUrl() { touched = true; writeHash(); return location.href; }

function summary() {
    if (!R) return '';
    const { c } = R;
    const lines = [
        tr('copy_head', { country: countryName(S.c) }),
        `${tr('st_gross', { tax: taxOf(c, R.cr) })}: ${money(R.res.gross, c)}`,
        `${$('#vc-tax-l').textContent}: ${money(R.res.tax, c)}`,
        `${tr('st_net', { tax: taxOf(c, R.cr) })}: ${money(R.res.net, c)}`,
        formula(),
    ];
    const w = wordsOf(R.res.gross, c, { style: 'digits_words' });
    if (w) lines.push(w);
    lines.push(shareUrl());
    return lines.join('\n');
}

let completeTimer = 0, lastComplete = '';
function scheduleComplete() {
    clearTimeout(completeTimer);
    completeTimer = setTimeout(() => {
        const key = [S.c, S.mode, S.amt, S.rate].join('|');
        if (key === lastComplete) return;
        lastComplete = key;
        try { window.__aeToolComplete && window.__aeToolComplete({ country: S.c, mode: S.mode, rate: R ? V.ppmToString(R.cr.rate.ppm) : '' }); } catch (e) { /* optional */ }
    }, 1500);
}

/* ───────────────────────────── страна ───────────────────────────── */

let regionNames = null;
function countryName(code) {
    try { regionNames = regionNames || new Intl.DisplayNames([LOC], { type: 'region' }); return regionNames.of(code); }
    catch (e) { return code; }
}
/* Страна по умолчанию — по языку страницы; внутри языка — по часовому поясу человека
   (es: Мадрид → Испания, Мехико → Мексика), если он не выбрал сам и не пришёл по ссылке. */
function countryFromTz() {
    let tz = '';
    try { tz = Intl.DateTimeFormat().resolvedOptions().timeZone || ''; } catch (e) { return null; }
    const own = (W.byLocale || {})[PAGE] || [];
    for (const code of own) {
        const list = (W.tz || {})[code] || [];
        if (list.some((z) => (z.endsWith('/') ? tz.startsWith(z) : tz === z))) return code;
    }
    return null;
}
function caProvinceFromTz() {
    try {
        const tz = Intl.DateTimeFormat().resolvedOptions().timeZone || '';
        let p = (W.caTz || {})[tz] || null;
        if (p === 'ON' && PAGE === 'fr') p = 'QC';          // Монреаль живёт в поясе America/Toronto
        return p;
    } catch (e) { return null; }
}
function setCountry(code, why) {
    if (!CT[code]) return;
    S.c = code;
    S.rate = null;
    S.inside = null;
    if (code === 'CA') {
        const p = caProvinceFromTz();
        if (p) { const k = (CT.CA.rates || []).findIndex((r) => (r.prov || []).includes(p)); if (k >= 0) S.rate = k; }
    }
    const sel = $('#vc-country');
    if (sel && sel.value !== code) sel.value = code;
    renderRates();
    renderCountryCard();
    paintLocal();
    linesRates();
    compute();
    if (why === 'user') { touched = true; saveSettings(); ae('country', { c: code }); }
}

function renderCountryCard() {
    const box = $('#vc-country-card');
    if (!box) return;
    const c = country(), code = S.c;
    $('#vc-country-title').textContent = tr('card_title', { country: countryName(code) });
    const rows = [];
    if (c.kind === 'not_vat' || c.kind === 'sales_tax') rows.push(`<p class="tc-msg warn">${esc(tr('cnote_' + code))}</p>`);
    if ((c.rates || []).length) {
        rows.push(`<div class="tc-tablewrap"><table class="tc-table vc-rt"><thead><tr><th>${esc(tr('card_rate'))}</th><th>${esc(tr('card_what'))}</th></tr></thead><tbody>`
            + c.rates.map((r) => {
                const what = r.lbl ? r.lbl : (r.t || []).map((k) => tr('cat_' + k)).join(', ');
                const shown = r.dpp ? `${pctNum(V.ratePpm(r.r))} % × ${r.dpp[0]}/${r.dpp[1]} = ${pct(effPpm(V.makeRate({ r: r.r, dpp: { num: r.dpp[0], den: r.dpp[1] } })))}` : pct(V.ratePpm(r.r));
                const parts = r.parts ? ` <span class="vc-sub">(${r.parts.map(([id, x]) => id + ' ' + pct(V.ratePpm(x))).join(' + ')})</span>` : '';
                return `<tr><td class="num">${iso(shown)}${parts}</td><td>${esc(what)}</td></tr>`;
            }).join('') + `</tbody></table></div>`);
    }
    for (const th of c.th || []) rows.push(`<p class="vc-th"><span class="vc-th-l">${esc(tr('card_th'))}</span> ${esc(thLine(th, c))}</p>`);
    if (c.after) rows.push(`<p class="tc-hint">${esc(tr(c.after.k, { n: c.after.n, p: c.after.p }))}</p>`);
    if (c.thLaw) rows.push(`<p class="tc-hint">${esc(tr('card_law'))}: ${esc(c.thLaw)}</p>`);
    for (const n of c.notes || []) if (!(c.kind === 'not_vat' || c.kind === 'sales_tax') || n !== code) {
        const t = tr('cnote_' + n);
        if (!t.startsWith('cnote_')) rows.push(`<p class="tc-msg">${esc(t)}</p>`);
    }
    if (c.law) rows.push(`<p class="tc-hint">${esc(tr('card_rates_law'))}: ${esc(c.law)}</p>`);
    if ((c.src || []).length) rows.push(`<p class="tc-attrib">${esc(tr('card_src', { date: W.checked || '' }))} ${c.src.map((s) => `<a href="${esc(s.u)}" target="_blank" rel="noopener nofollow">${esc(s.w)}</a>`).join(' · ')}</p>`);
    box.innerHTML = rows.join('');
}
function whole(n, c) {
    try { return iso(nf(numLoc(c), { style: 'currency', currency: c.cur, maximumFractionDigits: 0 }).format(n)); }
    catch (e) { return iso(n + ' ' + c.cur); }
}
function thLine(th, c) {
    const a = th.a != null ? whole(th.a, c) : '';
    const b = th.b != null ? whole(th.b, c) : '';
    const scope = th.s ? tr('scope_' + th.s) + ': ' : '';
    return scope + tr('th_' + th.k, { a, b });
}

/* ───────────────────────────── таблица позиций ───────────────────────────── */

const LN = { rows: [], mode: 'gross', fit: 'none' };
function blankRow() { return { name: '', qty: '1', price: '', sum: '', rate: '' }; }
function linesRates() {
    // Список ставок для строк — ставки страны (без «иной базы» Индонезии нельзя: она же ставка).
    const sel = $('#vc-ln-rate');
    if (!sel) return;
    const c = country();
    const list = rateList(c);
    const cur = currentRate(c);
    sel.innerHTML = list.map((x) => `<option value="${x.i}"${cur && cur.i === x.i ? ' selected' : ''}>${x.src.dpp ? pct(effPpm(x.rate)) : pct(x.rate.ppm)}${x.src.lbl ? ' · ' + esc(x.src.lbl) : ''}</option>`).join('');
    renderLines();
}
function lineRate(row, c) {
    const list = rateList(c);
    if (row.rate !== '' && row.rate != null) {
        const m = String(row.rate).replace(',', '.').match(/^\d+(\.\d+)?$/);
        if (m) {
            const hit = list.find((x) => V.ppmToString(x.rate.ppm) === V.ppmToString(V.ratePpm(m[0])) && !x.src.dpp && !x.rate.parts);
            return hit ? hit.rate : V.makeRate(c.inside ? { r: m[0], inside: true } : m[0]);
        }
    }
    const k = Number(($('#vc-ln-rate') || {}).value || 0);
    return (list[k] || list[0] || { rate: V.makeRate(0) }).rate;
}
function linesCompute() {
    const c = country();
    const sep = sepOf(numLoc(c));
    const rows = [];
    LN.rows.forEach((r, idx) => {
        const q = V.parseAmount(r.qty || '1', { sep, decimals: 6 });
        const p = r.price !== '' ? V.parseAmount(r.price, { sep, decimals: c.dec }) : { ok: false };
        const s = r.sum !== '' ? V.parseAmount(r.sum, { sep, decimals: c.dec }) : { ok: false };
        if (!s.ok && !p.ok) return;
        rows.push({ idx, name: r.name, qty: q.ok ? { n: q.minor, d: 10n ** 6n } : { n: 1n, d: 1n }, price: p.ok ? p.minor : null, sum: s.ok ? s.minor : null, rate: lineRate(r, c) });
    });
    if (!rows.length) return null;
    const t = V.lineTotals(rows, LN.mode);
    const f = LN.fit !== 'none' && t.groups.some((g) => g.diff !== 0n) ? V.fitLines(t, LN.fit, LN.mode) : null;
    return { t, f, c };
}
function renderLines() {
    const body = $('#vc-ln-body');
    if (!body) return;
    if (!LN.rows.length) LN.rows = [blankRow(), blankRow(), blankRow()];
    const c = country();
    const out = linesCompute();
    const byIdx = new Map();
    if (out) (out.f || out.t).rows.forEach((r) => byIdx.set(r.idx, r));
    body.innerHTML = LN.rows.map((r, i) => {
        const x = byIdx.get(i);
        return `<tr data-i="${i}">
<td class="vc-ln-n">${i + 1}</td>
<td><input class="tc-in vc-ln-in" data-k="name" value="${esc(r.name)}" aria-label="${esc(tr('ln_name'))}" autocomplete="off"></td>
<td><input class="tc-in vc-ln-in num" data-k="qty" value="${esc(r.qty)}" inputmode="decimal" aria-label="${esc(tr('ln_qty'))}" autocomplete="off"></td>
<td><input class="tc-in vc-ln-in num" data-k="price" value="${esc(r.price)}" inputmode="decimal" aria-label="${esc(tr('ln_price'))}" autocomplete="off"></td>
<td><input class="tc-in vc-ln-in num" data-k="sum" value="${esc(r.sum)}" placeholder="${x && r.sum === '' ? esc(plain(x.amount, c)) : ''}" inputmode="decimal" aria-label="${esc(tr('ln_sum'))}" autocomplete="off"></td>
<td><input class="tc-in vc-ln-in num vc-ln-rt" data-k="rate" value="${esc(r.rate)}" placeholder="${x ? esc(pctNum(x.rate.ppm)) : ''}" inputmode="decimal" aria-label="${esc(tr('ln_rate'))}" autocomplete="off"></td>
<td class="num vc-ln-tax">${x ? plain(x.tax, c) : ''}</td>
<td><button type="button" class="vc-ln-del" data-del="${i}" aria-label="${esc(tr('ln_del'))}">×</button></td></tr>`;
    }).join('');
    paintLineTotals(out);
}
function paintLineTotals(out) {
    const box = $('#vc-ln-tot');
    if (!box) return;
    if (!out) { box.innerHTML = `<p class="tc-hint">${esc(tr('ln_empty'))}</p>`; return; }
    const { t, f, c } = out;
    const fin = f || t;
    const parts = [`<div class="tc-stats vc-ln-stats">
<div class="tc-stat"><div class="tc-stat-l">${esc(tr('st_net', { tax: c.tax }))}</div><div class="tc-stat-v">${money(fin.net, c)}</div></div>
<div class="tc-stat lead"><div class="tc-stat-l">${esc(tr('ln_tax_total', { tax: c.tax }))}</div><div class="tc-stat-v">${money(fin.tax, c)}</div></div>
<div class="tc-stat"><div class="tc-stat-l">${esc(tr('st_gross', { tax: c.tax }))}</div><div class="tc-stat-v">${money(fin.gross, c)}</div></div></div>`];
    if (t.groups.length > 1) parts.push(`<ul class="vc-parts">${t.groups.map((g) => `<li><span>${esc(tr('ln_by_rate', { rate: pct(g.rate.ppm), tax: c.tax }))}</span><b>${money(f ? f.rows.filter((r) => g.rows.some((x) => x.idx === r.idx)).reduce((s, r) => s + r.tax, 0n) : g.taxLines, c)}</b></li>`).join('')}</ul>`);
    const diffs = t.groups.filter((g) => g.diff !== 0n);
    if (diffs.length) {
        const g = diffs[0];
        parts.push(`<p class="tc-msg warn">${esc(tr('ln_diff', { lines: money(diffs.reduce((s, x) => s + x.taxLines, 0n), c), total: money(diffs.reduce((s, x) => s + x.taxFromTotal, 0n), c), diff: money(diffs.reduce((s, x) => s + x.diff, 0n), c), tax: c.tax }))}</p>`);
        parts.push(`<label class="tc-f vc-mt"><span class="tc-f-l">${esc(tr('ln_fit'))}</span><select class="tc-in" id="vc-ln-fit">${['none', 'largest_remainder', 'largest', 'last'].map((k) => `<option value="${k}"${LN.fit === k ? ' selected' : ''}>${esc(tr('fit_' + k))}</option>`).join('')}</select></label>`);
        parts.push(`<p class="tc-hint">${esc(tr(c === CT.RU ? 'ln_diff_ru' : 'ln_diff_hint'))}</p>`);
        void g;
    } else if (LN.fit !== 'none') LN.fit = 'none';
    box.innerHTML = parts.join('');
}
function linesFromText(text, at) {
    const c = country();
    const parsed = V.parseTable(text, { sep: sepOf(numLoc(c)), decimals: c.dec });
    if (!parsed.rows.length) { toast(tr('ln_paste_none'), 'warning'); return 0; }
    const rows = parsed.rows.map((r) => ({
        name: r.name || '',
        qty: r.qty ? fracText(r.qty) : '1',
        price: r.price != null ? plain(r.price, c).replace(/[\u2066\u2069]/g, '') : '',
        sum: r.sum != null ? plain(r.sum, c).replace(/[\u2066\u2069]/g, '') : '',
        rate: r.ratePct || '',
    }));
    const keep = LN.rows.slice(0, at).filter((r) => r.name || r.price || r.sum);
    LN.rows = keep.concat(rows);
    renderLines();
    draftTouch(true);
    ae('lines_paste', { n: rows.length });
    return rows.length;
}
function fracText(q) {
    // Количество обратно в строку записью страны: 1,5 — а не 1500000/1000000.
    const n = q.n, d = q.d;
    if (n % d === 0n) return (n / d).toString();
    const s = V.minorToDecimal(V.roundDiv(n * 1000000n, d), 6).replace(/0+$/, '').replace(/\.$/, '');
    return sepOf(numLoc(country())) === 'en' ? s : s.replace('.', ',');
}
function linesCsv(delim) {
    const out = linesCompute();
    if (!out) return '';
    const { c } = out;
    const fin = out.f || out.t;
    const head = [tr('ln_name'), tr('ln_qty'), tr('ln_price'), tr('ln_sum'), tr('ln_rate'), tr('st_net', { tax: c.tax }), c.tax, tr('st_gross', { tax: c.tax })];
    const num = (m) => {
        // В CSV для Excel — десятичный знак записи страны: русский Excel не прочтёт «18032.79».
        const s = V.minorToDecimal(m, c.dec);
        return sepOf(numLoc(c)) === 'en' ? s : s.replace('.', ',');
    };
    const lines = [head.map((h) => V.csvCell(h, delim)).join(delim)];
    for (const r of fin.rows) {
        lines.push([r.name, fracText(r.qty), r.price != null ? num(r.price) : '', num(r.amount), pctNum(r.rate.ppm), num(r.net), num(r.tax), num(r.gross)].map((v) => V.csvCell(v, delim)).join(delim));
    }
    lines.push([tr('ln_total'), '', '', num(fin.rows.reduce((s, r) => s + r.amount, 0n)), '', num(fin.net), num(fin.tax), num(fin.gross)].map((v) => V.csvCell(v, delim)).join(delim));
    return lines.join('\r\n');
}

/* ───────────────────────────── пересчёт прайса ───────────────────────────── */

const PR = { text: '', from: '20', to: '22', basis: 'gross', rule: 'kop', col: null };
function priceRun() {
    const c = country();
    const out = $('#vc-pr-out');
    if (!out) return;
    const text = $('#vc-pr-text').value;
    PR.text = text;
    if (!text.trim()) { out.innerHTML = `<p class="tc-hint">${esc(tr('pr_empty'))}</p>`; return null; }
    let from, to;
    const mk = (v) => { v = String(v).replace(',', '.'); return V.makeRate(c.inside ? { r: v, inside: true } : v); };
    try { from = mk(PR.from); to = mk(PR.to); }
    catch (e) { out.innerHTML = `<p class="tc-msg warn">${esc(tr('why_rate_bad'))}</p>`; return null; }
    const sep = sepOf(numLoc(c));
    const delim = V.detectDelimiter(text) || '\t';
    const rows = text.split(/\r?\n/).filter((l) => l.trim()).map((l) => V.splitCsvLine(l, delim));
    // Столбец цены — последний столбец, где большинство ячеек — числа.
    const width = Math.max(...rows.map((r) => r.length));
    let col = PR.col;
    if (col == null || col >= width) {
        col = 0;
        for (let k = width - 1; k >= 0; k--) {
            const nums = rows.filter((r) => r[k] && V.parseAmount(r[k], { sep, decimals: c.dec }).ok && !/[a-zа-яё]{2,}/i.test(r[k])).length;
            if (nums >= Math.max(1, rows.length * 0.5)) { col = k; break; }
        }
    }
    const res = [];
    let up = 0n, n = 0;
    for (const r of rows) {
        const cell = r[col];
        const p = cell ? V.parseAmount(cell, { sep, decimals: c.dec }) : { ok: false };
        if (!p.ok || /[a-zа-яё]{2,}/i.test(cell || '')) { res.push({ r, head: true }); continue; }
        const x = V.repriceOne(p.minor, from, to, PR.basis, PR.rule, c.dec);
        res.push({ r, x });
        up += x.neu - (PR.basis === 'net' ? x.oldGross : x.old); n++;
    }
    PR.last = { res, col, delim, c, from, to };
    const head = res.find((z) => z.head);
    const th = `<tr>${(head ? head.r : rows[0].map((_, i) => '#' + (i + 1))).map((h, i) => `<th${i === col ? ' class="vc-pr-col"' : ''}>${esc(h)}</th>`).join('')}<th>${esc(tr(PR.basis === 'net' ? 'pr_new_gross' : 'pr_new'))}</th>${PR.rule === 'keep' || PR.basis === 'gross' ? `<th>${esc(tr('pr_net_change'))}</th>` : ''}</tr>`;
    const body = res.filter((z) => !z.head).slice(0, 200).map((z) => `<tr>${z.r.map((v, i) => `<td${i === col ? ' class="num"' : ''}>${esc(v)}</td>`).join('')}<td class="num"><b>${plain(z.x.neu, c)}</b></td>${PR.rule === 'keep' || PR.basis === 'gross' ? `<td class="num">${z.x.netChange != null ? plain(z.x.netChange, c) : ''}</td>` : ''}</tr>`).join('');
    const more = n > 200 ? `<p class="tc-hint">${esc(tr('pr_more', { n: n - 200 }))}</p>` : '';
    // множитель цены с налогом: (100 + новая) / (100 + старая), у «по дентро» — (100 − старая) / (100 − новая)
    const factor = c.inside ? `${pctNum(V.FULL - from.ppm)} / ${pctNum(V.FULL - to.ppm)}` : `${pctNum(to.ppm + V.FULL)} / ${pctNum(from.ppm + V.FULL)}`;
    out.innerHTML = `<p class="tc-hint">${esc(tr(PR.basis === 'net' ? 'pr_sum_net' : 'pr_sum', { n, f: factor, col: col + 1 }))}</p>
<div class="tc-tablewrap vc-pr-wrap"><table class="tc-table">${th}${body}</table></div>${more}`;
    ae('price', { n, rule: PR.rule });
    return PR.last;
}
function priceCsv() {
    const L = PR.last;
    if (!L) return '';
    const c = L.c;
    const num = (m) => { const s = V.minorToDecimal(m, c.dec); return sepOf(numLoc(c)) === 'en' ? s : s.replace('.', ','); };
    // Свой разделитель файла сохраняем; вставку из Excel (табуляция) — тем, что ждёт Excel человека.
    const d = L.delim === '\t' ? csvDelim(c) : L.delim;
    return L.res.map((z) => {
        if (z.head) return z.r.concat([tr(PR.basis === 'net' ? 'pr_new_gross' : 'pr_new')]).map((v) => V.csvCell(v, d)).join(d);
        return z.r.concat([num(z.x.neu)]).map((v) => V.csvCell(v, d)).join(d);
    }).join('\r\n');
}

/* ───────────────────────────── файлы CSV ───────────────────────────── */

async function readTextFile(file) {
    let buf;
    try { buf = window.FileBytes && window.FileBytes.arrayBuffer ? await window.FileBytes.arrayBuffer(file) : await file.arrayBuffer(); }
    catch (e) { buf = await file.arrayBuffer(); }
    const bytes = new Uint8Array(buf);
    // UTF-8 (с BOM или без); иначе — Windows-1251: так сохраняет CSV русский Excel.
    try { return new TextDecoder('utf-8', { fatal: true }).decode(bytes).replace(/^\uFEFF/, ''); }
    catch (e) {
        const enc = /^(ru|uk)$/.test(PAGE) || ['RU', 'UA', 'KZ', 'BY', 'UZ'].includes(S.c) ? 'windows-1251' : 'windows-1252';
        try { return new TextDecoder(enc).decode(bytes); } catch (e2) { return new TextDecoder('utf-8').decode(bytes); }
    }
}
function bindUploader(id, cb) {
    const el = document.getElementById(id);
    if (!el) return;
    const h = (f) => { if (f) readTextFile(f).then(cb).catch((err) => { try { window.__handleError ? window.__handleError(err, 'vat_csv_read', { say: tr('file_read_fail') }) : toast(tr('file_read_fail'), 'error'); } catch (e) { /* optional */ } }); };
    if (typeof window.__bindUploaderFile === 'function') window.__bindUploaderFile(el, (f) => h(f));
    else el.addEventListener('tool-uploader:file', (e) => h(e.detail && e.detail.file));
}
function download(text, name) {
    const blob = new Blob(['\uFEFF' + text], { type: 'text/csv;charset=utf-8' });
    if (window.__downloadFile) window.__downloadFile(blob, name);
    ae('download', { name: name.split('.').pop() });
}

/* ═══════════════════════════ Россия: УСН и переход ═══════════════════════════ */

const RU = W.ru || null;           // строки модуля (только на русской странице)
const ruT = (k, rep = {}) => String(RU && RU[k] != null ? RU[k] : k).replace(/:([a-z_][a-z_0-9]*)/g, (m, n) => (rep[n] != null ? rep[n] : m));
const MONTHS = () => { try { const f = new Intl.DateTimeFormat(LOC, { month: 'long' }); return Array.from({ length: 12 }, (_, i) => f.format(new Date(Date.UTC(2026, i, 15)))); } catch (e) { return Array.from({ length: 12 }, (_, i) => String(i + 1)); } };
const MONTHS_GEN = () => { try { const f = new Intl.DateTimeFormat(LOC, { day: 'numeric', month: 'long' }); return Array.from({ length: 12 }, (_, i) => f.format(new Date(Date.UTC(2026, i, 1)))); } catch (e) { return MONTHS(); } };
const rubBig = (minor) => rub(Number(minor) / 100, CT.RU);
// Пороги — «272,5 млн ₽», а не «272 500 000 ₽»: их сверяют с законом, где они написаны так.
const rubMln = (minor) => { const r = Number(minor) / 100; return r >= 1e6 && r % 1e5 === 0 ? ruT('mln', { v: nf(LOC, { maximumFractionDigits: 1 }).format(r / 1e6) }) : rubBig(minor); };
const qName = (q) => ruT('q_name', { q: q.q, y: q.y });

function ruMonthsGrid() {
    const g = $('#vc-usn-months');
    if (!g || g.childElementCount) return;
    g.innerHTML = MONTHS().map((m, i) => `<label class="tc-f"><span class="tc-f-l">${esc(m)}</span><input class="tc-in" data-month="${i}" inputmode="decimal" autocomplete="off" placeholder="0"></label>`).join('');
}
function ruParseRub(v) {
    const r = V.parseAmount(v, { sep: 'ru', decimals: 2 });
    return r.ok ? r.minor : null;
}
function usnRun() {
    if (!RU) return;
    const out = $('#vc-usn-out');
    const year = Number($('#vc-usn-year').value) || 2026;
    const prevRaw = $('#vc-usn-prev').value;
    const prev = ruParseRub(prevRaw);
    const months = $$('#vc-usn-months input').map((el) => (el.value.trim() ? ruParseRub(el.value) : null));
    const interest = $('#vc-usn-ip').checked ? ruParseRub($('#vc-usn-interest').value) : null;
    $('#vc-usn-interest-wrap').hidden = !$('#vc-usn-ip').checked;
    const t = V.usnTimeline({ year, prevIncome: prev, months, newBiz: $('#vc-usn-new').checked, excise: $('#vc-usn-excise').checked, depositInterest: interest });
    const L = t.limits;
    const gen = MONTHS_GEN();
    const lines = [];
    const st = t.start.status;
    if (t.interestOff) lines.push(`<p class="tc-msg">${esc(ruT('usn_interest_off', { inc: rubBig(t.prevCounted) }))}</p>`);
    if (t.start.reason === 'prev_unknown') lines.push(`<p class="tc-msg warn">${esc(ruT('usn_need_prev', { y: year - 1 }))}</p>`);
    const startText = {
        exempt: ruT(t.start.reason === 'new_business' ? 'usn_start_new' : 'usn_start_exempt', { y: year, prev: rubMln(L.exPrev), py: year - 1 }),
        r5: ruT(t.start.reason === 'excise' ? 'usn_start_excise' : 'usn_start_vat5', { y: year, prev: rubMln(L.exPrev), py: year - 1, r5: rubMln(L.prevR5) }),
        r7: ruT('usn_start_vat7', { y: year, py: year - 1, r5: rubMln(L.prevR5), r7: rubMln(L.prevR7) }),
        general: ruT('usn_start_general', { py: year - 1, r7: rubMln(L.prevR7) }),
    }[st];
    // Заголовок — главное событие года: потеря УСН, начало НДС, иначе — статус на 1 января.
    const lost = t.events.find((e) => e.type === 'usn_lost');
    const vatIn = t.events.find((e) => e.type === 'vat_from' && e.exceeded);
    const headV = lost ? ruT('usn_head_lost', { m: gen[lost.month - 1] }) : vatIn ? ruT('usn_head_vat', { m: gen[vatIn.month - 1], rate: vatIn.rate }) : ruT('usn_status_' + st);
    const headL = lost || vatIn ? ruT('usn_ans_year', { y: year }) : ruT('usn_ans_l', { y: year });
    lines.push(`<div class="tc-answer"><p class="tc-answer-l">${esc(headL)}</p><div class="tc-answer-v vc-usn-v">${esc(headV)}</div><p class="tc-answer-sub">${esc(startText)}</p></div>`);
    for (const e of t.events) {
        if (e.type === 'vat_from' && e.exceeded) lines.push(`<p class="tc-msg bad">${esc(ruT('usn_ev_vat', { m: gen[e.month - 1], ex: MONTHS()[e.exceeded - 1], lim: rubMln(L.exIn), rate: e.rate, cum: rubBig(e.cum) }))}</p>`);
        if (e.type === 'vat_next_year') lines.push(`<p class="tc-msg warn">${esc(ruT('usn_ev_vat_next', { y: year + 1, lim: rubMln(L.exIn) }))}</p>`);
        if (e.type === 'to7_from') lines.push(`<p class="tc-msg warn">${esc(ruT('usn_ev_to7', { m: gen[e.month - 1], ex: MONTHS()[e.exceeded - 1], lim: rubMln(L.r5) }))}</p>`);
        if (e.type === 'to7_next_year') lines.push(`<p class="tc-msg warn">${esc(ruT('usn_ev_to7_next', { y: year + 1, lim: rubMln(L.r5) }))}</p>`);
        if (e.type === 'usn_lost') lines.push(`<p class="tc-msg bad">${esc(ruT('usn_ev_lost', { m: gen[e.month - 1], lim: rubMln(L.r7) }))}</p>`);
    }
    if (t.monthsKnown) {
        const left = st === 'exempt' && !t.events.some((e) => e.type === 'vat_from' || e.type === 'vat_next_year') && t.total <= L.exIn ? L.exIn - t.total : null;
        if (left != null) lines.push(`<p class="tc-msg good">${esc(ruT('usn_left', { left: rubBig(left), lim: rubMln(L.exIn) }))}</p>`);
        lines.push(`<div class="tc-tablewrap"><table class="tc-table vc-usn-t"><thead><tr><th>${esc(ruT('usn_t_month'))}</th><th class="num">${esc(ruT('usn_t_income'))}</th><th class="num">${esc(ruT('usn_t_cum'))}</th><th>${esc(ruT('usn_t_status'))}</th></tr></thead><tbody>`
            + t.months.map((m) => `<tr class="vc-st-${m.status}"><td>${esc(MONTHS()[m.m - 1])}</td><td class="num">${m.value != null ? rubBig(m.value) : '—'}</td><td class="num">${m.cum != null ? rubBig(m.cum) : '—'}</td><td>${esc(ruT('usn_status_' + m.status))}</td></tr>`).join('') + '</tbody></table></div>');
    }
    if (t.next) lines.push(`<p class="tc-msg">${esc(ruT('usn_next_' + t.next.status, { y: t.next.year, inc: rubBig(t.next.income) }))}</p>`);
    if (!L.known) lines.push(`<p class="tc-hint">${esc(ruT('usn_deflator_unknown', { y: year }))}</p>`);
    lines.push(`<p class="tc-hint">${esc(ruT('usn_limits', { ex: rubMln(L.exIn), r5: rubMln(L.r5), r7: rubMln(L.r7), y: year }))}</p>`);
    out.innerHTML = lines.join('');
    ae('usn_monitor', { st });
}

function cmpRun() {
    if (!RU) return;
    const out = $('#vc-cmp-out');
    const num = (id) => { const r = V.parseAmount($(id).value, { sep: 'ru', decimals: 2 }); return r.ok ? Number(r.minor) / 100 : 0; };
    const o = {
        revenue: num('#vc-cmp-rev'),
        purchases: num('#vc-cmp-purch'),
        costs: num('#vc-cmp-costs'),
        model: $('#vc-cmp-model .on') ? $('#vc-cmp-model .on').dataset.model : 'b2c',
        obj: $('#vc-cmp-obj .on') ? $('#vc-cmp-obj .on').dataset.obj : 'income',
        special: Number($('#vc-cmp-special').value) || 5,
        outRate: Number($('#vc-cmp-out-rate').value) || 22,
        inRate: Number($('#vc-cmp-in-rate').value) || 22,
        minTax: true,
    };
    o.usnRate = Number(String($('#vc-cmp-usn').value).replace(',', '.')) || (o.obj === 'net' ? 15 : 6);
    $('#vc-cmp-costs-wrap').hidden = o.obj !== 'net';
    if (!o.revenue) { out.innerHTML = `<p class="tc-hint">${esc(ruT('cmp_empty'))}</p>`; return; }
    const c = V.usnCompare(o);
    const be = V.usnBreakEven(o);
    const share = o.purchases / o.revenue;
    const sp = o.special;
    const L = [];
    const better = c.better === 'general' ? ruT('cmp_better_general') : c.better === 'special' ? ruT('cmp_better_special', { s: sp }) : ruT('cmp_equal');
    L.push(`<div class="tc-answer"><p class="tc-answer-l">${esc(ruT('cmp_ans_l'))}</p><div class="tc-answer-v vc-usn-v">${esc(better)}</div><p class="tc-answer-sub">${esc(c.better === 'equal' ? '' : ruT('cmp_diff', { d: rub(Math.abs(c.diff), CT.RU) }))}</p></div>`);
    L.push(`<div class="tc-tablewrap"><table class="tc-table"><thead><tr><th></th><th class="num">${esc(ruT('cmp_col_general', { r: o.outRate }))}</th><th class="num">${esc(ruT('cmp_col_special', { s: sp }))}</th></tr></thead><tbody>
<tr><td>${esc(ruT('cmp_row_vat'))}</td><td class="num">${rub(c.general.vat, CT.RU)}</td><td class="num">${rub(c.special.vat, CT.RU)}</td></tr>
<tr><td>${esc(ruT('cmp_row_usn', { u: o.usnRate }))}</td><td class="num">${rub(c.general.usn, CT.RU)}</td><td class="num">${rub(c.special.usn, CT.RU)}</td></tr>
<tr><td><b>${esc(ruT('cmp_row_burden'))}</b></td><td class="num"><b>${rub(c.general.burden, CT.RU)}</b></td><td class="num"><b>${rub(c.special.burden, CT.RU)}</b></td></tr>
<tr><td>${esc(ruT('cmp_row_profit'))}</td><td class="num">${rub(c.general.profit, CT.RU)}</td><td class="num">${rub(c.special.profit, CT.RU)}</td></tr>
${o.model === 'b2b' ? `<tr><td>${esc(ruT('cmp_row_buyer'))}</td><td class="num">${rub(c.general.buyerPays, CT.RU)}</td><td class="num">${rub(c.special.buyerPays, CT.RU)}</td></tr>` : ''}
</tbody></table></div>`);
    if (be && be.share != null && o.model !== 'b2b') {
        L.push(`<p class="tc-msg">${esc(ruT('cmp_breakeven', { z: nf(LOC, { maximumFractionDigits: 1 }).format(be.share * 100), now: nf(LOC, { maximumFractionDigits: 1 }).format(share * 100), s: sp, r: o.outRate }))}</p>`);
    } else if (o.model === 'b2b') {
        L.push(`<p class="tc-msg">${esc(ruT('cmp_b2b'))}</p>`);
    } else if (be && be.always) {
        L.push(`<p class="tc-msg">${esc(ruT(be.always === 'general' ? 'cmp_always_general' : 'cmp_always_special', { s: sp }))}</p>`);
    }
    // Двенадцать кварталов, на которые закон связывает выбор 5/7 %, и до какого квартала можно передумать.
    const fy = Number($('#vc-cmp-fy').value) || 2026, fq = Number($('#vc-cmp-fq').value) || 1;
    const growth = Number(String($('#vc-cmp-growth').value).replace(',', '.')) || 0;
    const lock = V.rateLock({ y: fy, q: fq });
    const fc = V.usnForecast({ ...o, growth, first: { y: fy, q: fq } });
    L.push(`<p class="tc-msg warn">${esc(ruT('cmp_lock', { first: qName(lock.first), refuse: qName(lock.refuseUntil), through: qName(lock.lockedThrough), free: qName(lock.freeFrom), s: sp }))}</p>`);
    L.push(`<div class="tc-tablewrap"><table class="tc-table vc-fc"><thead><tr><th>${esc(ruT('fc_q'))}</th><th class="num">${esc(ruT('fc_rev'))}</th><th class="num">${esc(ruT('fc_diff'))}</th><th class="num">${esc(ruT('fc_cum'))}</th></tr></thead><tbody>`
        + fc.map((r, i) => `<tr${i === 3 ? ' class="now"' : ''}><td>${esc(qName(r))}${i === 3 ? ' · ' + esc(ruT('fc_refuse_last')) : ''}</td><td class="num">${rub(r.revenue, CT.RU)}</td><td class="num">${rub(r.diff, CT.RU)}</td><td class="num">${rub(r.cum, CT.RU)}</td></tr>`).join('') + '</tbody></table></div>');
    L.push(`<p class="tc-hint">${esc(ruT('fc_note'))}</p>`);
    out.innerHTML = L.join('');
    ae('usn_compare', { model: o.model, obj: o.obj, s: sp, better: c.better });
}

function trRun() {
    if (!RU) return;
    const out = $('#vc-tr-out');
    const price = ruParseRub($('#vc-tr-price').value);
    const adv = ruParseRub($('#vc-tr-adv').value) || 0n;
    if (!price) { out.innerHTML = `<p class="tc-hint">${esc(ruT('tr_empty'))}</p>`; return; }
    const raise = $('#vc-tr-raise .on') ? $('#vc-tr-raise .on').dataset.raise === '1' : true;
    const t = V.transition({ price, advance: adv > price ? price : adv, raise });
    const m = (x) => money(x, CT.RU, 'ru-RU');
    const L = [];
    L.push(`<div class="tc-stats"><div class="tc-stat"><div class="tc-stat-l">${esc(ruT('tr_adv_vat'))}</div><div class="tc-stat-v">${m(t.adv.tax)}</div><div class="tc-stat-s">${esc(ruT('tr_adv_vat_s'))}</div></div>
<div class="tc-stat lead"><div class="tc-stat-l">${esc(ruT('tr_ship_vat'))}</div><div class="tc-stat-v">${m(t.shipment.tax)}</div><div class="tc-stat-s">${esc(ruT('tr_ship_vat_s', { g: m(t.shipment.gross) }))}</div></div>
<div class="tc-stat"><div class="tc-stat-l">${esc(ruT('tr_deduct'))}</div><div class="tc-stat-v">${m(t.deductAdvance)}</div><div class="tc-stat-s">${esc(ruT('tr_deduct_s'))}</div></div></div>`);
    if (raise) L.push(`<p class="tc-msg">${esc(ruT('tr_raise', { extra: m(t.extraFromBuyer), rest: m(t.remainder), g: m(t.shipment.gross) }))}</p>`);
    else L.push(`<p class="tc-msg warn">${esc(ruT('tr_same', { loss: m(t.netLoss), net: m(t.shipment.net) }))}</p>`);
    L.push(`<ul class="vc-list">${['tr_rule_ship', 'tr_rule_buyer', 'tr_rule_corr', 'tr_rule_contract'].map((k) => `<li>${esc(ruT(k))}</li>`).join('')}</ul>`);
    out.innerHTML = L.join('');
    ae('transition', { raise: raise ? 1 : 0 });
}

/* ═══════════════════════════ Корея: 간이과세자 ═══════════════════════════ */

const KR = W.kr || null;
function krRun() {
    if (!KR) return;
    const out = $('#vc-kr-out');
    const c = CT.KR;
    const p = V.parseAmount($('#vc-kr-sales').value, { sep: 'en', decimals: 0 });
    const buy = V.parseAmount($('#vc-kr-buy').value || '0', { sep: 'en', decimals: 0 });
    const ratio = Number($('#vc-kr-ratio').value) || 15;
    if (!p.ok) { out.innerHTML = ''; return; }
    // 납부세액 = 공급대가 × 업종별 부가가치율 × 10% − 매입 공급대가 × 0.5% (0 미만이면 0, 환급 없음)
    const due = p.minor * BigInt(ratio) * 10n / 1000n;
    const credit = buy.ok ? buy.minor * 5n / 1000n : 0n;
    let tax = due - credit;
    if (tax < 0n) tax = 0n;
    const exempt = p.minor < BigInt(c.krSimple.exemptBelow);
    const kt = (k, rep = {}) => String(KR[k] != null ? KR[k] : k).replace(/:([a-z_][a-z_0-9]*)/g, (m, n) => (rep[n] != null ? rep[n] : m));
    out.innerHTML = `<div class="tc-answer"><p class="tc-answer-l">${esc(kt('kr_ans_l'))}</p><div class="tc-answer-v">${exempt ? esc(kt('kr_exempt_v')) : money(tax, c)}</div>
<p class="tc-answer-sub">${esc(kt('kr_formula', { s: money(p.minor, c), r: ratio, due: money(due, c), cr: money(credit, c) }))}</p></div>
${exempt ? `<p class="tc-msg good">${esc(kt('kr_exempt', { lim: money(BigInt(c.krSimple.exemptBelow), c) }))}</p>` : ''}
<p class="tc-hint">${esc(kt('kr_note'))}</p>`;
    ae('kr_simple', { ratio });
}

/* ───────────────────────────── местные модули ───────────────────────────── */

function paintLocal() {
    $$('[data-local]').forEach((el) => { el.hidden = el.dataset.local !== S.c; });
}

/* ───────────────────────────── черновик таблицы ───────────────────────────── */

let drafts = null;
function draftTouch(counted) { if (drafts) drafts.touch(counted); }
function bootDrafts() {
    if (!window.__toolDraft || !window.__toolDraft.editor || !$('#vcDrafts')) return;
    drafts = window.__toolDraft.editor({
        slug: SLUG, root: 'vcDrafts', icon: '🧾',
        payload: () => {
            const filled = LN.rows.filter((r) => r.name || r.price || r.sum);
            if (!filled.length && !PR.text) return null;
            return { state: { c: S.c, rows: LN.rows, mode: LN.mode, fit: LN.fit, pr: { text: $('#vc-pr-text') ? $('#vc-pr-text').value : '', from: PR.from, to: PR.to, basis: PR.basis, rule: PR.rule } },
                label: (filled[0] && filled[0].name ? filled[0].name : tr('ln_title')).slice(0, 80), meta: { rows: filled.length } };
        },
        apply: async (d) => {
            const st = d.state || {};
            if (st.c && CT[st.c] && st.c !== S.c) setCountry(st.c);
            LN.rows = Array.isArray(st.rows) && st.rows.length ? st.rows.map((r) => ({ ...blankRow(), ...r })) : [blankRow(), blankRow(), blankRow()];
            LN.mode = st.mode === 'net' ? 'net' : 'gross';
            LN.fit = st.fit || 'none';
            if (st.pr) { Object.assign(PR, st.pr); if ($('#vc-pr-text')) $('#vc-pr-text').value = st.pr.text || ''; syncPriceControls(); if (st.pr.text) priceRun(); }
            syncLinesMode();
            renderLines();
            return [];
        },
        fresh: () => { LN.rows = [blankRow(), blankRow(), blankRow()]; LN.fit = 'none'; if ($('#vc-pr-text')) $('#vc-pr-text').value = ''; PR.text = ''; renderLines(); if ($('#vc-pr-out')) $('#vc-pr-out').innerHTML = ''; },
        started: () => LN.rows.some((r) => r.name || r.price || r.sum) || !!($('#vc-pr-text') && $('#vc-pr-text').value.trim()),
    });
    drafts.boot();
}
function syncLinesMode() {
    $$('#vc-ln-mode .tc-seg-b').forEach((b) => { const on = b.dataset.lnmode === LN.mode; b.classList.toggle('on', on); b.setAttribute('aria-pressed', on); });
}
function syncPriceControls() {
    if ($('#vc-pr-from')) $('#vc-pr-from').value = PR.from;
    if ($('#vc-pr-to')) $('#vc-pr-to').value = PR.to;
    if ($('#vc-pr-rule')) $('#vc-pr-rule').value = PR.rule;
    $$('#vc-pr-basis .tc-seg-b').forEach((b) => { const on = b.dataset.basis === PR.basis; b.classList.toggle('on', on); b.setAttribute('aria-pressed', on); });
}

/* ───────────────────────────── настройки ───────────────────────────── */

function saveSettings() {
    try { window.__toolSettings && window.__toolSettings.save(SLUG, 'prefs', JSON.stringify({ c: S.c, words: S.words })); } catch (e) { /* storage off */ }
}
function loadSettings() {
    try {
        const raw = window.__toolSettings ? window.__toolSettings.load(SLUG, 'prefs', '') : '';
        if (!raw) return null;
        const o = JSON.parse(raw);
        if (o.words && typeof o.words === 'object') Object.assign(S.words, o.words);
        return o.c && CT[o.c] ? o.c : null;
    } catch (e) { return null; }
}

/* ───────────────────────────── проводка ───────────────────────────── */

function setMode(m) {
    S.mode = m;
    $$('#vc-mode .tc-seg-b').forEach((b) => { const on = b.dataset.mode === m; b.classList.toggle('on', on); b.setAttribute('aria-pressed', on); });
    $('#vc-amt-l').textContent = tr('amt_' + m, { tax: taxOf(country(), currentRate(country())) });
    compute();
}

function wire() {
    const amt = $('#vc-amt');
    amt.addEventListener('input', () => { S.amt = amt.value; touched = true; compute(); });
    amt.addEventListener('focus', () => stage('amount'));
    $('#vc-mode').addEventListener('click', (e) => { const b = e.target.closest('[data-mode]'); if (!b) return; touched = true; setMode(b.dataset.mode); ae('mode', { m: b.dataset.mode }); });
    $('#vc-rates').addEventListener('click', (e) => {
        const b = e.target.closest('[data-rate]');
        if (!b) return;
        touched = true;
        S.rate = b.dataset.rate === 'custom' ? 'custom' : Number(b.dataset.rate);
        renderRates();
        if (S.rate === 'custom') $('#vc-custom').focus();
        compute();
        ae('rate', { r: b.dataset.rate });
    });
    $('#vc-custom').addEventListener('input', (e) => { S.custom = e.target.value; touched = true; compute(); });
    // Своя ставка «внутри цены» или сверху (Бразилия: ICMS и ISS — по дентро, IPI и CBS/IBS — по фора).
    $('#vc-inside').addEventListener('click', (e) => {
        const b = e.target.closest('[data-inside]');
        if (!b) return;
        S.inside = b.dataset.inside === 'in';
        touched = true;
        renderRates(); compute(); linesRates();
        ae('inside', { v: b.dataset.inside });
    });
    // Сумма, набранная подряд («1000000»), после ввода читается разрядами («1 000 000»):
    // так человек сверяет порядок, не считая нули. Выражение остаётся как набрано.
    amt.addEventListener('blur', () => {
        if (!R || R.input.expr || !R.input.ok) return;
        const c = country();
        const minor = R.input.minor;
        const frac = minor % (10n ** BigInt(c.dec)) !== 0n;
        let txt;
        try { txt = nf(numLoc(c), { minimumFractionDigits: frac ? c.dec : 0, maximumFractionDigits: c.dec }).format(V.minorToDecimal(minor, c.dec)); } catch (e) { return; }
        if (txt === amt.value) return;
        const back = V.parseAmount(txt, { sep: sepOf(numLoc(c)), decimals: c.dec });
        if (!back.ok || back.minor !== minor) return;          // запись, которую мы не прочтём обратно, не подставляем
        amt.value = txt; S.amt = txt; writeHash();
    });
    $('#vc-split').addEventListener('click', (e) => { const b = e.target.closest('[data-split]'); if (!b) return; S.split = b.dataset.split; touched = true; renderRates(); compute(); linesRates(); });
    $('#vc-country').addEventListener('change', (e) => setCountry(e.target.value, 'user'));
    $('#vc-line-copy').addEventListener('click', () => copy($('#vc-line').textContent, 'line'));
    $('#vc-copy-all').addEventListener('click', () => copy(summary(), 'summary'));
    $('#vc-share').addEventListener('click', () => {
        const url = shareUrl();
        ae('share');
        if (navigator.share && matchMedia('(pointer:coarse)').matches) navigator.share({ title: document.title, text: summary(), url }).catch(() => { /* closed */ });
        else copy(url, 'link');
    });
    // Плитки: нажатие копирует число как есть, без валюты — в поле бухгалтерской программы.
    $$('[data-copy-fig]').forEach((el) => el.addEventListener('click', () => {
        if (!R) return;
        const m = R.res[el.dataset.copyFig];
        copy(plain(m, R.c).replace(/[\u2066\u2069]/g, ''), 'fig_' + el.dataset.copyFig);
    }));
    // Прописью
    $('#vc-words-fig').addEventListener('click', (e) => { const b = e.target.closest('[data-fig]'); if (!b) return; S.words.fig = b.dataset.fig; paintWords(); saveSettings(); });
    $('#vc-words-style').addEventListener('change', (e) => { S.words.style = e.target.value; paintWords(); saveSettings(); });
    $('#vc-words-minor').addEventListener('change', (e) => { S.words.minor = e.target.value; paintWords(); saveSettings(); });
    $('#vc-words-cap').addEventListener('change', (e) => { S.words.cap = e.target.checked; paintWords(); saveSettings(); });
    $('#vc-words-copy').addEventListener('click', () => copy($('#vc-words-out').textContent, 'words'));
    // Фразы
    $('#vc-phr-list').addEventListener('click', (e) => {
        const b = e.target.closest('[data-copy-phr]');
        if (!b) return;
        const it = ($('#vc-phr-list')._items || [])[+b.dataset.copyPhr];
        if (it) copy(it.text, 'phrase_' + it.key);
    });
    // Позиции
    const lnBody = $('#vc-ln-body');
    if (lnBody) {
        lnBody.addEventListener('input', (e) => {
            const inp = e.target.closest('[data-k]');
            if (!inp) return;
            const i = +inp.closest('tr').dataset.i;
            LN.rows[i][inp.dataset.k] = inp.value;
            // Перерисовать только числа, не поля: курсор человека остаётся на месте.
            const out = linesCompute();
            const c = country();
            const by = new Map();
            if (out) (out.f || out.t).rows.forEach((r) => by.set(r.idx, r));
            $$('#vc-ln-body tr').forEach((tr2) => {
                const x = by.get(+tr2.dataset.i);
                tr2.querySelector('.vc-ln-tax').textContent = x ? plain(x.tax, c) : '';
                const s = tr2.querySelector('[data-k=sum]');
                if (s && s.value === '') s.placeholder = x ? plain(x.amount, c).replace(/[\u2066\u2069]/g, '') : '';
                const rt = tr2.querySelector('[data-k=rate]');
                if (rt && rt.value === '') rt.placeholder = x ? pctNum(x.rate.ppm) : '';
            });
            paintLineTotals(out);
            draftTouch(false);
        });
        lnBody.addEventListener('paste', (e) => {
            const text = (e.clipboardData || window.clipboardData).getData('text');
            if (!/[\t\n]/.test(text)) return;
            e.preventDefault();
            const i = +e.target.closest('tr').dataset.i;
            const n = linesFromText(text, i);
            if (n) toast(tr('ln_pasted', { n }), 'success');
        });
        lnBody.addEventListener('click', (e) => {
            const b = e.target.closest('[data-del]');
            if (!b) return;
            LN.rows.splice(+b.dataset.del, 1);
            renderLines();
            draftTouch(true);
        });
        $('#vc-ln-add').addEventListener('click', () => { LN.rows.push(blankRow()); renderLines(); const last = $$('#vc-ln-body tr').pop(); if (last) last.querySelector('input').focus(); });
        $('#vc-ln-clear').addEventListener('click', () => { LN.rows = [blankRow(), blankRow(), blankRow()]; LN.fit = 'none'; renderLines(); draftTouch(true); });
        $('#vc-ln-mode').addEventListener('click', (e) => { const b = e.target.closest('[data-lnmode]'); if (!b) return; LN.mode = b.dataset.lnmode; syncLinesMode(); renderLines(); draftTouch(true); });
        $('#vc-ln-rate').addEventListener('change', () => { renderLines(); draftTouch(true); });
        $('#vc-ln-tot').addEventListener('change', (e) => { if (e.target.id === 'vc-ln-fit') { LN.fit = e.target.value; renderLines(); draftTouch(true); ae('lines_fit', { how: LN.fit }); } });
        $('#vc-ln-csv').addEventListener('click', () => { const t = linesCsv(csvDelim(country())); if (t) download(t, tr('ln_file') + '.csv'); else toast(tr('ln_empty'), 'warning'); });
        $('#vc-ln-tsv').addEventListener('click', () => { const t = linesCsv('\t'); if (t) copy(t, 'lines_tsv'); else toast(tr('ln_empty'), 'warning'); });
        $('#vc-ln-pastebtn').addEventListener('click', () => { const w = $('#vc-ln-pastebox'); w.hidden = !w.hidden; if (!w.hidden) $('#vc-ln-paste').focus(); });
        // панель не закрываем: в ней же аплоадер CSV, и его строка «Другой файл» уходила бы вместе с ней
        $('#vc-ln-paste-go').addEventListener('click', () => { const n = linesFromText($('#vc-ln-paste').value, 0); if (n) { toast(tr('ln_pasted', { n }), 'success'); $('#vc-ln-paste').value = ''; } });
        bindUploader('vcLnDrop', (text) => { const n = linesFromText(text, 0); if (n) toast(tr('ln_pasted', { n }), 'success'); });
    }
    // Прайс
    if ($('#vc-pr-text')) {
        const rerun = () => { PR.from = $('#vc-pr-from').value; PR.to = $('#vc-pr-to').value; PR.rule = $('#vc-pr-rule').value; priceRun(); draftTouch(false); };
        $('#vc-pr-text').addEventListener('input', () => { PR.col = null; rerun(); });
        ['#vc-pr-from', '#vc-pr-to', '#vc-pr-rule'].forEach((id) => $(id).addEventListener('change', rerun));
        $('#vc-pr-basis').addEventListener('click', (e) => { const b = e.target.closest('[data-basis]'); if (!b) return; PR.basis = b.dataset.basis; syncPriceControls(); rerun(); });
        $('#vc-pr-csv').addEventListener('click', () => { const t = priceCsv(); if (t) download(t, tr('pr_file') + '.csv'); else toast(tr('pr_empty'), 'warning'); });
        $('#vc-pr-tsv').addEventListener('click', () => {
            const L = PR.last;
            if (!L) { toast(tr('pr_empty'), 'warning'); return; }
            // Один столбец новых цен — вставить рядом со старым в Excel.
            const t = L.res.map((z) => (z.head ? tr(PR.basis === 'net' ? 'pr_new_gross' : 'pr_new') : V.minorToDecimal(z.x.neu, L.c.dec).replace('.', sepOf(numLoc(L.c)) === 'en' ? '.' : ','))).join('\n');
            copy(t, 'price_col');
        });
        bindUploader('vcPrDrop', (text) => { $('#vc-pr-text').value = text; PR.col = null; rerun(); });
    }
    // Россия
    if (RU) {
        ruMonthsGrid();
        const u = () => usnRun();
        ['#vc-usn-year', '#vc-usn-new', '#vc-usn-excise', '#vc-usn-ip'].forEach((id) => $(id) && $(id).addEventListener('change', u));
        $('#vc-usn-prev').addEventListener('input', u);
        $('#vc-usn-interest').addEventListener('input', u);
        $('#vc-usn-months').addEventListener('input', u);
        $('#vc-usn-months').addEventListener('paste', (e) => {
            // Двенадцать чисел из столбца Excel — по месяцам с того, куда вставили.
            const text = (e.clipboardData || window.clipboardData).getData('text');
            const vals = text.split(/[\r\n\t;]+/).map((s) => s.trim()).filter(Boolean);
            if (vals.length < 2) return;
            e.preventDefault();
            const start = +e.target.dataset.month || 0;
            const ins = $$('#vc-usn-months input');
            vals.slice(0, 12 - start).forEach((v, k) => { ins[start + k].value = v; });
            usnRun();
        });
        const cmp = () => cmpRun();
        ['#vc-cmp-rev', '#vc-cmp-purch', '#vc-cmp-costs', '#vc-cmp-usn', '#vc-cmp-growth'].forEach((id) => $(id).addEventListener('input', cmp));
        ['#vc-cmp-special', '#vc-cmp-out-rate', '#vc-cmp-in-rate', '#vc-cmp-fy', '#vc-cmp-fq'].forEach((id) => $(id).addEventListener('change', cmp));
        for (const g of ['#vc-cmp-model', '#vc-cmp-obj']) $(g).addEventListener('click', (e) => {
            const b = e.target.closest('.tc-seg-b');
            if (!b) return;
            $$(g + ' .tc-seg-b').forEach((x) => { const on = x === b; x.classList.toggle('on', on); x.setAttribute('aria-pressed', on); });
            if (g === '#vc-cmp-obj') $('#vc-cmp-usn').value = b.dataset.obj === 'net' ? '15' : '6';
            cmp();
        });
        const trr = () => trRun();
        ['#vc-tr-price', '#vc-tr-adv'].forEach((id) => $(id).addEventListener('input', trr));
        $('#vc-tr-raise').addEventListener('click', (e) => {
            const b = e.target.closest('.tc-seg-b');
            if (!b) return;
            $$('#vc-tr-raise .tc-seg-b').forEach((x) => { const on = x === b; x.classList.toggle('on', on); x.setAttribute('aria-pressed', on); });
            trr();
        });
        // Ящики оживают при открытии: пример уже в полях, ответ — сразу.
        $('#vc-d-usn').addEventListener('toggle', (e) => { if (e.target.open) { usnRun(); cmpRun(); stage('usn'); } });
        $('#vc-d-tr').addEventListener('toggle', (e) => { if (e.target.open) { trRun(); stage('transition'); } });
    }
    if (KR) {
        ['#vc-kr-sales', '#vc-kr-buy'].forEach((id) => $(id).addEventListener('input', krRun));
        $('#vc-kr-ratio').addEventListener('change', krRun);
        $('#vc-d-kr').addEventListener('toggle', (e) => { if (e.target.open) krRun(); });
    }
    for (const id of ['#vc-d-phr', '#vc-d-lines', '#vc-d-price', '#vc-d-country']) {
        const d = $(id);
        if (d) d.addEventListener('toggle', (e) => { if (e.target.open) { stage(id.slice(5)); ae('open', { d: id.slice(5) }); } });
    }
    window.addEventListener('hashchange', () => { if (readHash()) { syncAll(); compute(); } });
}

/* Широкая консоль (≥ 1480 px: три колонки) — фразы для документа стоят рядом с ответом открытыми,
   пока человек не закрыл их сам. Узкая — ящик как был: закрыт, пока не нужен. */
let phrTouched = false, phrAuto = false;
function wideSide() {
    const root = $('#vc'), d = $('#vc-d-phr');
    if (!root || !d || phrTouched) return;
    const wide = root.getBoundingClientRect().width >= 1480;
    if (wide && !d.open) { phrAuto = true; d.open = true; }
    else if (!wide && d.open && phrAuto) { phrAuto = true; d.open = false; }
}
function watchWide() {
    const d = $('#vc-d-phr');
    if (d) d.addEventListener('toggle', () => { if (phrAuto) { phrAuto = false; return; } phrTouched = true; });
    wideSide();
    const root = $('#vc');
    if (root && window.ResizeObserver) new ResizeObserver(() => wideSide()).observe(root);
    if (root) root.addEventListener('wide-workspace:change', () => { wideSide(); ae('wide', { w: Math.round(root.getBoundingClientRect().width / 10) * 10 }); });
}

function syncAll() {
    const sel = $('#vc-country');
    if (sel) sel.value = S.c;
    $('#vc-amt').value = S.amt;
    $('#vc-custom').value = S.custom;
    $('#vc-words-style').value = S.words.style;
    $('#vc-words-minor').value = S.words.minor;
    $('#vc-words-cap').checked = !!S.words.cap;
    renderRates();
    renderCountryCard();
    paintLocal();
    setMode(S.mode);
}

/* ───────────────────────────── старт ───────────────────────────── */

function boot() {
    // Набранное до модуля — человека: его не перетирает ни пример, ни ссылка.
    const amtEl = $('#vc-amt');
    const typed = amtEl && amtEl.value !== amtEl.defaultValue ? amtEl.value : null;
    const fromLink = readHash();
    if (!fromLink) {
        const saved = loadSettings();
        // Сохранённая страна — только если человек выбирал её сам; иначе — пояс внутри языка.
        if (saved) S.c = saved;
        else { const tz = countryFromTz(); if (tz) S.c = tz; }
        if (S.c === 'CA') { const p = caProvinceFromTz(); if (p) { const k = (CT.CA.rates || []).findIndex((r) => (r.prov || []).includes(p)); if (k >= 0) S.rate = k; } }
    } else touched = true;
    if (!CT[S.c]) S.c = W.country;
    if (S.c !== W.country && !fromLink) S.amt = (W.examples || {})[S.c] || S.amt;
    if (typed != null) { S.amt = typed; touched = true; }
    linesRates();
    syncAll();
    syncLinesMode();
    syncPriceControls();
    wire();
    bootDrafts();
    watchWide();
    stage('ready');
}
boot();
